/* Оболочка сайта: шапка, подвал, курсор, прогресс прокрутки, слой анимаций. */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useStore } from '../store/StoreContext.jsx';
import { useCountUp, reducedMotion } from '../hooks/useMotion.js';
import { useUI } from '../store/UIContext.jsx';

/* ───────────────────────  Прогресс прокрутки  ─────────────────── */

function ScrollProgress() {
  const ref = useRef(null);

  useEffect(() => {
    const bar = ref.current;
    const header = document.getElementById('siteHeader');
    let ticking = false;

    const update = () => {
      ticking = false;
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (bar) bar.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`;
      header?.classList.toggle('is-stuck', y > 12);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return <div className="scroll-progress" ref={ref} />;
}

/* ──────────────────────────  Курсор  ──────────────────────────── */

function Cursor() {
  const rootRef = useRef(null);

  useEffect(() => {
    const cursor = rootRef.current;
    if (!cursor || reducedMotion() || window.matchMedia('(pointer: coarse)').matches) return;

    const dot = cursor.querySelector('.cursor__dot');
    const ring = cursor.querySelector('.cursor__ring');
    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    let rx = x;
    let ry = y;
    let raf = 0;

    const move = (e) => {
      x = e.clientX;
      y = e.clientY;
      cursor.classList.add('is-on');
      dot.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
    };
    const leave = () => cursor.classList.remove('is-on');
    const down = () => cursor.classList.add('is-down');
    const up = () => cursor.classList.remove('is-down');
    const over = (e) => {
      const hot = e.target.closest('a, button, input, select, textarea, .product-card__media, .size, .chip, .pvz, .option');
      cursor.classList.toggle('is-hot', !!hot);
    };

    const loop = () => {
      rx += (x - rx) * 0.16;
      ry += (y - ry) * 0.16;
      ring.style.transform = `translate(${rx}px, ${ry}px) translate(-50%, -50%)`;
      raf = requestAnimationFrame(loop);
    };
    loop();

    document.addEventListener('mousemove', move, { passive: true });
    document.addEventListener('mouseleave', leave);
    document.addEventListener('pointerdown', down);
    document.addEventListener('pointerup', up);
    document.addEventListener('mouseover', over);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseleave', leave);
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('mouseover', over);
    };
  }, []);

  return (
    <div className="cursor" ref={rootRef} aria-hidden="true">
      <span className="cursor__dot" />
      <span className="cursor__ring" />
    </div>
  );
}

/* ───────────────────────────  Шапка  ──────────────────────────── */

function Header({ onBrand }) {
  const { user, cart } = useStore();
  const location = useLocation();
  const navRef = useRef(null);
  const inkRef = useRef(null);
  const countRef = useCountUp(cart.count);
  const [menu, setMenu] = useState(false);

  const isCatalog = location.pathname === '/' || location.pathname.startsWith('/product');

  // Меню закрывается при переходе, по Esc и не даёт странице прокручиваться
  useEffect(() => setMenu(false), [location.pathname]);

  useEffect(() => {
    if (!menu) return;
    const onKey = (e) => { if (e.key === 'Escape') setMenu(false); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  // Подчёркивание активного пункта переезжает под «Каталог»
  useLayoutEffect(() => {
    const ink = inkRef.current;
    const nav = navRef.current;
    if (!ink || !nav) return;

    const active = nav.querySelector('.nav__item.is-active');
    if (!active) {
      ink.classList.remove('is-on');
      return;
    }
    const move = () => {
      const base = nav.getBoundingClientRect();
      const r = active.getBoundingClientRect();
      ink.classList.add('is-on');
      ink.style.width = `${r.width}px`;
      ink.style.transform = `translateX(${r.left - base.left}px)`;
    };
    move();
    window.addEventListener('resize', move);
    return () => window.removeEventListener('resize', move);
  }, [isCatalog, location.pathname]);

  return (
    <header className="site-header" id="siteHeader">
      <div className="site-header__inner">
        <div className="site-header__left">
          <Link className="logo" to="/" aria-label="SAINTS — на главную">
            <span className="logo__text">SAINTS</span>
            <span className="logo__dot">.</span>
          </Link>
          <nav className="nav" ref={navRef} aria-label="Основная навигация">
            <NavLink className={`nav__item${isCatalog ? ' is-active' : ''}`} to="/">
              Каталог
            </NavLink>
            <button className="nav__item" type="button" onClick={onBrand}>
              О бренде
            </button>
            {user?.role === 'admin' ? (
              <NavLink className="nav__item" to="/admin">
                Админка
              </NavLink>
            ) : null}
            <span className="nav__ink" ref={inkRef} aria-hidden="true" />
          </nav>
        </div>

        <div className="site-header__right">
          <Link className="btn btn--ghost-dark btn--account" to={user ? '/account' : '/login'}>
            <span className="btn__label">{user ? user.name.split(' ')[0] : 'Войти'}</span>
          </Link>
          <Link className="btn btn--accent btn--cart" to="/cart" id="cartBtn">
            <span className="btn__label">
              <span className="btn__word">Корзина </span><span className="btn__sep">·</span>{' '}
              <span className="cart-count" ref={countRef}>{cart.count}</span>
            </span>
          </Link>

          <button
            type="button"
            className={`burger${menu ? ' is-open' : ''}`}
            aria-label={menu ? 'Закрыть меню' : 'Открыть меню'}
            aria-expanded={menu}
            aria-controls="mobileMenu"
            onClick={() => setMenu((v) => !v)}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      {/* Меню для узких экранов: на широких его заменяет обычная навигация */}
      <div
        id="mobileMenu"
        className={`mobile-menu${menu ? ' is-open' : ''}`}
        hidden={!menu}
      >
        <button
          type="button"
          className="mobile-menu__veil"
          aria-label="Закрыть меню"
          tabIndex={-1}
          onClick={() => setMenu(false)}
        />
        {/* Закрываем по нажатию, а не только по смене адреса: переход на
            текущую же страницу адрес не меняет, и меню оставалось открытым */}
        <nav className="mobile-menu__panel" aria-label="Меню" onClick={() => setMenu(false)}>
          <NavLink className={`mobile-menu__item${isCatalog ? ' is-active' : ''}`} to="/">
            Каталог
          </NavLink>
          <button className="mobile-menu__item" type="button" onClick={onBrand}>
            О бренде
          </button>
          <NavLink className="mobile-menu__item" to={user ? '/account' : '/login'}>
            {user ? 'Личный кабинет' : 'Войти'}
          </NavLink>
          {user?.role === 'admin' ? (
            <NavLink className="mobile-menu__item" to="/admin">
              Панель управления
            </NavLink>
          ) : null}
        </nav>
      </div>
    </header>
  );
}

/* ───────────────────────────  Подвал  ─────────────────────────── */

function Footer({ onSupport }) {
  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <Link className="logo logo--sm" to="/">
          <span className="logo__text">SAINTS</span>
          <span className="logo__dot">.</span>
        </Link>
        <div className="site-footer__mid">
          <span className="site-footer__center">© 2026 Saints · Доставка по РФ и СНГ</span>
          {/* Производство, оплата и отправка идут через OneMerch — указываем это
              на витрине, как и положено при работе на чужой платформе */}
          <a
            className="site-footer__by"
            href="https://onemerch.ru"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>Производство и доставка</span>
            <img src="/onm_logo.svg" alt="OneMerch" width="96" height="20" loading="lazy" />
          </a>
        </div>
        <div className="site-footer__links">
          <Link to="/delivery">Доставка</Link>
          <Link to="/returns">Возврат</Link>
          <Link to="/offer">Оферта</Link>
          <Link to="/privacy">Политика</Link>
          <button type="button" onClick={onSupport}>Поддержка</button>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────  Вся оболочка  ─────────────────────── */

export default function Layout() {
  const ui = useUI();
  const location = useLocation();

  // Каждый переход — наверх страницы
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, [location.pathname]);

  return (
    <>
      <ScrollProgress />
      <a className="skip-link" href="#main">Перейти к содержимому</a>

      <Header onBrand={ui.openBrand} />

      <main id="main" className="app">
        <Outlet />
      </main>

      <Footer onSupport={ui.openSupport} />

      <div className="fly-layer" id="flyLayer" aria-hidden="true" />
      <Cursor />
    </>
  );
}
